import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { OrderWithContext, OrderDeliveryInfo, OrderDeliveryInfoStatus, CustomerProfile } from '@/models';
import { Routes } from '@/router';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import moment from 'moment';

const DELIVERY_INFO_POLL_RATE_MS = 60 * 1000;
const MAX_DELIVERY_INFO_POLL_TIME_MS = 3 * 60 * 60 * 1000;

@Component({
  name: 'CmgOrderConfirmationPage',
})
export default class CmgOrderConfirmationPage extends Vue {
  deliveryInfoRefreshInterval: number | null = null;
  deliveryInfoRefreshStartTime: Date | null = null;
  order: OrderWithContext | null = null;
  deliveryInfo: OrderDeliveryInfo | null = null;

  get isReady(): boolean {
    return !!this.order && (!!this.order.order.delivery ? !!this.deliveryInfo : true);
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  async mounted() {
    await this.$services.customer.waitForCustomerInitialization();

    const orderId = this.$route.params['orderId'];

    try {
      this.order = await this.$services.order.getOrder(orderId);
    } catch (error: any) {
      this.$services.logger.warn('Could not find order on confirmation page', { orderId });
      this.handleInactiveOrNotFoundOrder();
      return;
    }

    if (!this.order) {
      this.handleInactiveOrNotFoundOrder();
      return;
    }

    if (this.order.order.delivery) {
      await this.refreshDeliveryInfo();
      this.deliveryInfoRefreshStartTime = new Date();
      this.deliveryInfoRefreshInterval = window.setInterval(() => {
        const timeDiff = new Date().getTime() - this.deliveryInfoRefreshStartTime!.getTime();
        if (timeDiff > MAX_DELIVERY_INFO_POLL_TIME_MS) {
          this.clearDeliveryInfoRefreshInterval();
        }
        this.refreshDeliveryInfo();
      }, DELIVERY_INFO_POLL_RATE_MS);
    }

    if (this.isInactiveorder()) {
      this.handleInactiveOrNotFoundOrder();
    }
  }

  destroyed() {
    this.clearDeliveryInfoRefreshInterval();
  }

  private async refreshDeliveryInfo(): Promise<void> {
    const deliveryInfo = this.order && this.order.order.delivery;

    // If somehow we called this method without any delivery info, clear the interval and abort
    if (!deliveryInfo) {
      this.clearDeliveryInfoRefreshInterval();
      return;
    }

    // Get the latest delivery info
    this.deliveryInfo = await this.$services.delivery.getDeliveryInfo(deliveryInfo.deliveryId);

    // If the order has been delivered, clear the refresh interval
    if (this.deliveryInfo.deliveryStatus === OrderDeliveryInfoStatus.Arrived || this.deliveryInfo.deliveryStatus === OrderDeliveryInfoStatus.Delivered) {
      this.clearDeliveryInfoRefreshInterval();
    }
  }

  private clearDeliveryInfoRefreshInterval() {
    if (this.deliveryInfoRefreshInterval != null) {
      window.clearInterval(this.deliveryInfoRefreshInterval);
    }
  }

  // This may have odd side affects for locations that border time zones
  private isInactiveorder(): boolean {
    const now = moment();
    const orderTime = this.deliveryInfo ? moment(this.deliveryInfo.quotedDeliveryTime || '') : moment(this.order!.order.pickupDateTimeLocal || '');
    return orderTime.add(3, 'hour') < now;
  }

  private handleInactiveOrNotFoundOrder(): void {
    this.$services.ui.setLastSelectedElement('submit-btn');
    this.$services.ui.setModalVisibility('basicError', true, {
      header: `THE ORDER YOU ARE LOOKING FOR ISN'T ACTIVE ANYMORE.`,
      content: 'We hope it was tasty.',
      options: [
        {
          text: 'Okay',
          type: ButtonType.Primary,
          onClick: () => this.$services.ui.setModalVisibility('basicError', false),
        },
      ],
    });

    this.$services.ui.firstFocus();
    this.$router.push({ name: Routes.Home });
  }
}
